import { useQuery } from '@tanstack/react-query';
import { analyticsApi, dashboardApi } from '../lib/api-client';

// Cache configuration constants
const FIVE_MINUTES = 5 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

export const useAnalytics = () => {
  return useQuery({
    queryKey: ['analytics'],
    queryFn: () => analyticsApi.getAnalytics(),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useLocationCleanliness = (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
  return useQuery({
    queryKey: ['analytics', 'location-cleanliness', params],
    queryFn: () => analyticsApi.getLocationCleanliness(params),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useFacilityTypeMetrics = (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
  return useQuery({
    queryKey: ['analytics', 'facility-type-metrics', params],
    queryFn: () => analyticsApi.getFacilityTypeMetrics(params),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useMapData = (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
  return useQuery({
    queryKey: ['analytics', 'map-data', params],
    queryFn: () => analyticsApi.getMapData(params),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useTrends = (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
  return useQuery({
    queryKey: ['analytics', 'trends', params],
    queryFn: () => analyticsApi.getTrends(params),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useQuestionnaireAnalytics = (
  questionnaireId: string,
  params?: {
    locationId?: string;
    startDate?: string;
    endDate?: string;
  }
) => {
  return useQuery({
    queryKey: ['analytics', 'questionnaire', questionnaireId, params],
    queryFn: () => analyticsApi.getQuestionnaireAnalytics(questionnaireId, params),
    enabled: !!questionnaireId,
    staleTime: FIFTEEN_MINUTES, // Data considered fresh for 15 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useLocationRanking = (params?: {
  locationType?: string;
  parentLocationId?: string;
  weekNumber?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'location-ranking', params],
    queryFn: () => analyticsApi.getLocationRanking(params),
    staleTime: TEN_MINUTES, // Data considered fresh for 10 minutes
    gcTime: THIRTY_MINUTES, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useLocationPerformance = (params?: {
  parentLocationId?: string;
  showFacilities?: boolean;
  locationType?: string;
  startDate?: string;
  endDate?: string;
  weekNumber?: number;
  year?: number;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'location-performance', params],
    queryFn: () => analyticsApi.getLocationPerformance(params),
    staleTime: FIVE_MINUTES, // Data considered fresh for 5 minutes
    gcTime: FIFTEEN_MINUTES, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

export const useLocationActivity = (params?: {
  locationType?: string;
  parentLocationId?: string;
  numberOfWeeks?: number;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'location-activity', params],
    queryFn: () => analyticsApi.getLocationActivity(params),
    staleTime: FIVE_MINUTES, // Data considered fresh for 5 minutes
    gcTime: FIFTEEN_MINUTES, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

// Analytics graph endpoints (chart-ready)
export const useHygieneTrend = (params?: {
  numberOfWeeks?: number;
  facilityTypeId?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'hygiene-trend', params],
    queryFn: () => analyticsApi.getHygieneTrend(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useHygieneScoreDistribution = (params?: {
  weekNumber?: number;
  year?: number;
  facilityTypeId?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'hygiene-score-distribution', params],
    queryFn: () => analyticsApi.getHygieneScoreDistribution(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useHygieneByFacilityType = (params?: {
  weekNumber?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'hygiene-by-facility-type', params],
    queryFn: () => analyticsApi.getHygieneByFacilityType(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceTrend = (params?: {
  numberOfWeeks?: number;
  facilityTypeId?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'compliance-trend', params],
    queryFn: () => analyticsApi.getComplianceTrend(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceScoreDistribution = (params?: {
  weekNumber?: number;
  year?: number;
  facilityTypeId?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'compliance-score-distribution', params],
    queryFn: () => analyticsApi.getComplianceScoreDistribution(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceByFacilityType = (params?: {
  weekNumber?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'compliance-by-facility-type', params],
    queryFn: () => analyticsApi.getComplianceByFacilityType(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceQuestionBreakdown = (params?: {
  facilityTypeId?: string;
  weekNumber?: number;
  year?: number;
}) => {
  return useQuery({
    queryKey: ['analytics', 'graphs', 'compliance-question-breakdown', params],
    queryFn: () => analyticsApi.getComplianceQuestionBreakdown(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// New endpoints
export const useHygieneSummary = (params?: {
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'hygiene-summary', params],
    queryFn: () => analyticsApi.getHygieneSummary(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceSummary = (params?: {
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'compliance-summary', params],
    queryFn: () => analyticsApi.getComplianceSummary(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDecisions = (params?: {
  startDate?: string;
  endDate?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
}) => {
  return useQuery({
    queryKey: ['analytics', 'decisions', params],
    queryFn: () => analyticsApi.getDecisions(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useFollowup = (params?: {
  startDate?: string;
  endDate?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
}) => {
  return useQuery({
    queryKey: ['analytics', 'followup', params],
    queryFn: () => analyticsApi.getFollowup(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useMapDataForType = (params?: {
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'map-data-typed', params],
    queryFn: () => analyticsApi.getMapDataForType(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useComplianceLocationRanking = (params?: {
  parentLocationId?: string;
  locationType?: string;
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
  weekNumber?: number;
  year?: number;
}) => {
  return useQuery({
    queryKey: ['analytics', 'compliance-location-ranking', params],
    queryFn: () => analyticsApi.getComplianceLocationRanking(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useAssessmentKPIs = (params?: {
  month?: string;
  type?: string;
  facilityTypeId?: string;
  geoLocationId?: string;
}) => {
  return useQuery({
    queryKey: ['dashboard', 'assessment-kpis', params],
    queryFn: () => analyticsApi.getAssessmentKPIs(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useFacilitiesRanking = (params?: {
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  limit?: number;
  direction?: 'top' | 'bottom';
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['analytics', 'facilities-ranking', params],
    queryFn: () => analyticsApi.getFacilitiesRanking(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useLocationActivityRanking = (params?: {
  granularity?: 'daily' | 'weekly' | 'monthly';
  from?: string;
  to?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  locationType?: string;
  parentGeoLocationId?: string;
  facilityTypeId?: string;
  limit?: number;
  page?: number;
}) => {
  return useQuery({
    queryKey: ['dashboard', 'location-activity-ranking', params],
    queryFn: () => dashboardApi.getLocationActivityRanking(params),
    staleTime: FIVE_MINUTES,
    gcTime: FIFTEEN_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};
